import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Badge } from '@/components/ui/badge';
import { Sparkles } from 'lucide-react';

interface Props {
  userId?: string | null;
  kind: 'listing' | 'project';
  /** Approval status of the item being reviewed; rejected items don't count toward quota. */
  approvalStatus?: string | null;
}

/**
 * Compact pill shown inside approval dialogs telling reviewers which package
 * the submitter is using and how this approval affects their quota.
 */
export function PackageQuotaBadge({ userId, kind, approvalStatus }: Props) {
  const { data } = useQuery({
    queryKey: ['package-quota-badge', userId],
    enabled: !!userId,
    queryFn: async () => {
      const { data } = await supabase.rpc('subscription_listing_summary' as any, { _user_id: userId });
      return ((data as any[]) || [])[0] || null;
    },
  });

  if (!userId) return null;
  if (!data) {
    return (
      <Badge variant="outline" className="text-xs gap-1">
        <Sparkles className="h-3 w-3" /> No active package
      </Badge>
    );
  }

  const quota = kind === 'project' ? data.project_quota : data.listing_quota;
  const used = kind === 'project' ? data.projects_used : data.listings_used;
  const willConsume = approvalStatus === 'rejected'; // re-approving a rejected item adds back to count

  return (
    <Badge variant="outline" className="text-xs gap-1 border-primary/40 text-primary">
      <Sparkles className="h-3 w-3" />
      {data.package_name}: {used}/{quota} {kind}s used
      {willConsume && quota > 0 && <span className="ml-1 text-muted-foreground">(approving will reuse a slot)</span>}
    </Badge>
  );
}
